import { useRef, useState, ChangeEvent, FocusEvent, MouseEvent } from "react";
import { observer } from "mobx-react-lite";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { note } from "@/endpoints/dryp/note";
import { Camera, X } from "lucide-react";

type Props = {
	structureId: Dryp.StructureId;
	onPosted?: (note: Dryp.Note) => void;
	className?: string;
};

export const AddNote: React.FC<Props> = observer(({ structureId, onPosted, className }) => {
	const [value, setValue] = useState("");
	const [file, setFile] = useState<File | null>(null);
	const [isFocused, setIsFocused] = useState(false);
	const max = 1024;
	const isActive = isFocused || value.length > 0 || !!file;

	const postNoteApi = useAsyncStateStore(note.addNote);
	const canSend = (value.trim().length >= 3 || !!file) && value.length <= max && !postNoteApi.loading;

	const containerRef = useRef<HTMLDivElement | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	// Focus is tracked on the whole composer, not just the textarea, so the
	// action row stays open while the user moves to the Attach or Post button.
	const handleComposerBlur = (blurEvent: FocusEvent<HTMLDivElement>) => {
		const nextFocused = blurEvent.relatedTarget as Node | null;
		if (nextFocused && containerRef.current?.contains(nextFocused)) return;
		setIsFocused(false);
	};

	// Some browsers do not focus a button on click, which would blur the
	// textarea and hide this button before the click lands. Keep the focus.
	const handleAttachMouseDown = (mouseEvent: MouseEvent<HTMLButtonElement>) => {
		mouseEvent.preventDefault();
	};

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.files?.[0]) setFile(e.target.files[0]);
	};

	const clearFileInput = () => {
		// Reset the input so picking the same file again still fires onChange.
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const removeFile = () => {
		setFile(null);
		clearFileInput();
	};

	const submit = async () => {
		if (!canSend) return;
		const description = value.trim();
		const payload = {
			structureId,
			description: description || undefined,
			file: file || undefined,
		} as const;

		try {
			const created = await postNoteApi.fetch(payload);
			setValue("");
			setFile(null);
			clearFileInput();
			onPosted?.(created);
		} catch {}
	};

	return (
		<div
			ref={containerRef}
			onFocus={() => setIsFocused(true)}
			onBlur={handleComposerBlur}
			className={cn("space-y-2", className)}
		>
			<textarea
				value={value}
				onChange={e => setValue(e.target.value)}
				placeholder="Add a note..."
				className={cn(
					"w-full px-3 text-sm bg-secondary/50 border border-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200",
					isActive ? "py-2 min-h-[60px]" : "py-1.5 h-[32px]",
					postNoteApi.loading && "opacity-80",
				)}
				maxLength={max}
				disabled={postNoteApi.loading}
				onKeyDown={e => {
					if (e.key === "Enter" && !e.shiftKey) {
						e.preventDefault();
						submit();
					}
				}}
			/>

			<div className={cn("flex items-center justify-between", !isActive && "hidden")}>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onMouseDown={handleAttachMouseDown}
						onClick={() => fileInputRef.current?.click()}
						className="inline-flex items-center text-xs h-7 gap-1 rounded-md px-2 text-muted-foreground hover:bg-accent"
						disabled={postNoteApi.loading}
					>
						<Camera className="w-3.5 h-3.5" />
						Attach
					</button>
					<input
						ref={fileInputRef}
						type="file"
						accept="image/*,video/*,application/pdf"
						className="hidden"
						onChange={handleFileChange}
					/>

					{file && (
						<span className="inline-flex items-center gap-1 max-w-[120px] text-xs px-2 py-1 rounded bg-muted text-foreground">
							<span className="truncate">{file.name}</span>
							<button type="button" onClick={removeFile} className="p-0.5 rounded hover:bg-background">
								<X className="w-3 h-3" />
							</button>
						</span>
					)}
				</div>

				<Button type="button" size="sm" onClick={submit} disabled={!canSend} className="h-7 text-xs px-3">
					Post
				</Button>
			</div>

			{postNoteApi.error && (
				<p className="text-xs text-red-500">{postNoteApi.error?.message ?? "Failed to post note"}</p>
			)}
		</div>
	);
});
